import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { getAuth, getIdTokenResult } from 'firebase/auth';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {

  constructor(private router: Router) { }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    const auth = getAuth(); // Get Firebase Auth instance
    const requiresAdmin = route.data['requiresAdmin'] || false; // Get the 'requiresAdmin' data from route configuration

    return new Observable<boolean>(observer => {
      const unsubscribe = auth.onAuthStateChanged(async user => {
        if (user) {
          const idTokenResult = await getIdTokenResult(user);
          if (idTokenResult.signInSecondFactor) {
            if (requiresAdmin) {
              // If the route requires admin access, check the custom claims
              try {
                const claims = idTokenResult.claims;

                if (claims && claims['admin'] === true) {
                  // User has the 'admin' claim, allow access
                  observer.next(true);
                  observer.complete();
                } else {
                  // User does not have 'admin' claim, redirect to 'not-authorized'
                  this.router.navigate(['/not-authorized']);
                  observer.next(false);
                  observer.complete();
                }
              } catch (error) {
                // Handle error (e.g., failed to get custom claims)
                console.error('Error fetching claims:', error);
                this.router.navigate(['/login']);
                observer.next(false);
                observer.complete();
              }
            } else {
              // If the route does not require admin access, allow access if logged in
              observer.next(true);
              observer.complete();
            }
          } else {
            // User is not authenticated, redirect to login
            this.router.navigate(['/login']);
            observer.next(false);
            observer.complete();
          }
        } else {
          // User is not authenticated, redirect to login
          this.router.navigate(['/login']);
          observer.next(false);
          observer.complete();
        }
      });

      return { unsubscribe };
    }).pipe(take(1));
  }
}
